import type { ProMessages } from './types';

export const proEn: ProMessages = {
  landing: {
    eyebrow: 'Pricing',
    standardName: 'Standard',
    standardTagline: 'Start without paying anything',
    standardCta: 'Start for free',
    proName: 'Pro',
    proBadge: '0% fees',
    proPrice: '$49.99',
    proPeriod: '/ month',
    proNote: 'No commitment · pay with GRAM (ex-TON) or Telegram Stars',
    proFeatures: [
      'Connect your own Telegram bot',
      '0% transaction fee on every payment',
      'Stars collected directly on your bot (Telegram payout from 1,000 Stars)',
      'Customers pay on your brand (@your_bot)',
      'Content, subscriptions and channel access delivered by your bot',
      'Withdrawals from your platform balance with no commission',
      'Priority support',
    ],
    proCta: 'Go Pro',
    payWith: 'Pay with GRAM (ex-TON) or Telegram Stars, activated automatically',
    stepsTitle: 'Your own bot connected in 3 minutes',
    steps: [
      { title: 'Go Pro', desc: 'Pay $49.99 in GRAM or Stars: the plan activates by itself.' },
      { title: 'Paste your bot token', desc: 'Create it with @BotFather and paste the token in your dashboard.' },
      { title: 'Get paid without commission', desc: 'Your payment links go through your bot: the Stars land with you.' },
    ],
    calcTitle: 'How much do you save with Pro?',
    calcSalesLabel: 'Monthly sales',
    calcStandard: 'Standard fees (3%)',
    calcPro: 'Pro subscription',
    calcSaving: 'Savings with Pro',
    calcBreakEven: 'Pro pays for itself from {amount} of monthly sales.',
    calcBelow: 'Below {amount} of monthly sales, the Standard plan is still cheaper.',
    faq: [
      {
        question: 'How does the personal bot work?',
        answer:
          'You create a bot with @BotFather and paste its token in your dashboard. Your payment links are then generated by your bot: customers pay you directly and the platform takes nothing.',
      },
      {
        question: 'Where do my Stars go with the Pro plan?',
        answer:
          'Straight to your bot’s Stars balance on Telegram. They never go through the platform, so there is no 3% fee. Note that Telegram sets the withdrawal rules for a bot’s Stars: at least 1,000 Stars, and only after a 21-day hold. Below 1,000 Stars, they stay on the bot.',
      },
      {
        question: 'How do I pay for and renew the Pro plan?',
        answer:
          '$49.99 for 30 days, in GRAM (ex-TON) or Telegram Stars. There is no automatic charge: renew whenever you want and the remaining days are added up.',
      },
      {
        question: 'What happens when the Pro plan expires?',
        answer:
          'New links go back through the platform bot (3%). Links already created with your bot keep working for your customers.',
      },
    ],
    seoSuffix: 'Pro plan: your own Telegram bot, 0% fees, $49.99/month.',
  },
  app: {
    menuLabel: 'Pro',
    eyebrow: 'Pro plan',
    title: 'Your bot, 0% fees',
    subtitle: 'Connect your own Telegram bot: Stars payments land directly with you, with no commission.',
    loadError: 'Could not load your plan.',
    statusFree: 'Standard plan',
    statusActive: 'Pro active',
    statusUntil: 'Until {date} · {days} days left',
    statusExpired: 'Pro expired on {date}',
    expiredNotice:
      'Your Pro plan has expired: new links go through the platform bot (3% fee). Renew to get your bot back.',
    priceLine: '{price} USD for {days} days',
    upgrade: 'Go Pro',
    renew: 'Extend by {days} days',
    benefitsTitle: 'What you unlock',
    benefits: [
      'Your own Telegram bot connected in a few clicks',
      '0% transaction fee, including on platform withdrawals',
      'Stars collected directly on your bot',
      'Customers see your brand, not ours',
      'Content, subscriptions and channel access delivered by your bot',
    ],
    payTitle: 'Choose how to pay',
    payTon: 'Pay with GRAM (ex-TON)',
    payTonSub: '≈ {amount} GRAM',
    payStars: 'Pay with Stars',
    payStarsSub: '{amount} ⭐',
    tonUnavailable: 'GRAM payment coming soon',
    checkoutError: 'Could not start the payment. Please try again in a moment.',
    tonTitle: 'Send exactly this amount in GRAM (ex-TON)',
    tonAmountLabel: 'Amount',
    tonAddressLabel: 'Address',
    tonMemoLabel: 'Comment (required)',
    tonMemoHint: 'Without this comment we cannot match your payment.',
    openWallet: 'Open in Tonkeeper',
    scanQr: 'or scan this QR code with your wallet',
    waiting: 'Waiting for payment… this page updates automatically.',
    quoteExpires: 'Amount guaranteed for {time}',
    quoteExpired: 'The rate has expired.',
    refreshQuote: 'Refresh amount',
    underpaid: 'Payment received but incomplete ({amount} GRAM). Contact support to settle it.',
    starsTitle: 'Pay inside Telegram',
    starsHint: 'A Telegram Stars payment window will open. Activation is instant.',
    starsPayNow: 'Pay {amount} ⭐',
    paid: 'Payment received! Your Pro plan is active.',
    cancel: 'Cancel',
    copied: 'Copied',
    botTitle: 'Your Telegram bot',
    botLocked: 'Available with the Pro plan.',
    botIntro: 'Connect the bot that will collect your sales.',
    botStep1: 'Open @BotFather and send /newbot',
    botStep2: 'Copy the token you get (123456789:AA…)',
    botStep3: 'Paste it below',
    tokenLabel: 'Bot token',
    tokenPlaceholder: '123456789:AA…',
    connect: 'Connect my bot',
    connecting: 'Connecting…',
    connectedTitle: 'Bot connected',
    webhookOk: 'Connection active',
    webhookKo: 'Connection needs repair',
    pendingUpdates: '{count} pending events',
    repair: 'Repair connection',
    repaired: 'Connection restored.',
    disconnect: 'Disconnect',
    disconnectConfirm: 'Disconnect @{bot}? Links already created with this bot will no longer be payable.',
    openBot: 'Open bot',
    botConnected: 'Bot connected: your next links will go through @{bot}.',
    botDisconnected: 'Bot disconnected.',
    botWarning:
      'Connecting replaces your bot’s current webhook. Do not use a bot that is already plugged into another service.',
    botInfoTitle: 'How it works',
    botInfo: [
      'Your new payment links are created by your bot.',
      'Stars land directly in your bot’s balance on Telegram: they never go through the platform, so 0% fees.',
      'You withdraw them yourself to a GRAM (ex-TON) wallet through Fragment, from your bot’s “Balance” section: Telegram requires at least 1,000 Stars, a 21-day hold and two-step verification.',
      'The dashboard keeps showing your sales and content delivery works with your bot.',
    ],
    withdrawFeeHint: 'Gross: {gross} Stars · Fee: 0 (Pro plan) · You receive: {net} Stars',
    directBadge: 'Direct (own bot)',
    directEarned: 'Collected on your bot',
    settingsCommissionPro: '0% (Pro plan active)',
    supportLabel: 'Trouble with a payment? Write to us:',
  },
};
